import { PropsWithChildren, ReactElement, ReactNode, useMemo } from "react";

import { withSubComponent } from "@magnetic/higher-order-components";
import { MdsTableColumn, Table, TableProps } from "@magnetic/table";
import type { PaginationProps } from "@magnetic/table/dist/types/types";
import type { CellContext, Row, RowData } from "@tanstack/react-table";
import { BulkActionBarProps } from "third-party/magnetic/table";

import { BaseColumnConfig } from "../tables/columnConfigs/baseColumnConfig";
import { DIMMABLE_TABLE_CELL_CLASS } from "../tables/columnConfigs/columnConfigUtils";

import { BaseTypedTableProps, Column } from "./typedTableUtil";

import "./typedMagneticTableWrapper.scss";

type MagneticTableProps<Column> = BaseTypedTableProps<Column> &
  object &
  Omit<TableProps<unknown>, "columns" | "data">;

interface ConditionalPaginationProps extends PropsWithChildren {
  readonly maxClientPage?: PaginationProps["defaultRecordsOption"]; // note: Magnetic used string, but our team prefers number from the wrapper layer
}

const ConditionalPagination = ({
  maxClientPage,
  children
}: ConditionalPaginationProps) => {
  if (maxClientPage) {
    return (
      // Nested tables will also show pagination as Magnetic is internally using Context API to build the pagination
      <Table.Pagination
        currentPage={1}
        defaultRecordsOption={maxClientPage.toString()}
        showNavigation="all"
        showOptions={true}
        showTotal={true}
      >
        {children}
      </Table.Pagination>
    );
  }

  return children;
};

/**
 * Magnetic wrapper for Bulk Action Bar - calls the privately imported component
 */
const wrappedBulkActionBar = ({
  children
}: BulkActionBarProps & PropsWithChildren<unknown>) => {
  if (
    typeof children !== "string" &&
    typeof children !== "number" &&
    typeof children !== "boolean" &&
    children !== null
  ) {
    return (
      <Table.BulkActionBar>{children as ReactElement}</Table.BulkActionBar>
    );
  }
  return null;
};

export const TypedMagneticTableWrapper = withSubComponent(
  function <V extends BaseColumnConfig<VT>, VT>(
    props: MagneticTableProps<Column<V, VT>>
  ) {
    const {
      columns,
      data = [],
      stickyHeader = true,
      className = "",
      rowSelection = {},
      maxClientPage,
      isLoading,
      defaultSortColumn,
      ...rest
    } = props;

    /**
     * Build client column configuration metadata for plugging into the Magnetic Table
     * Converts the ColumnConfig definition into a Magnetic Table format.
     */

    const columnModel = useMemo((): MdsTableColumn<unknown>[] => {
      return Object.entries(columns)
        .filter(([, dataType]) => {
          const configuration = dataType.getConfig();
          return !configuration.hidden;
        })
        .map((field): MdsTableColumn<unknown> => {
          const [fieldName, dataType] = field;
          const configuration = dataType.getConfig();
          // Optional: if an alternate is configured, evaluate the first logical rule that meets conditional check against cell value
          const matchPriorityAlternativeConditional = (cellValue: VT) =>
            configuration.alternatives?.find((x) => x.check(cellValue));

          return {
            /**
             * Header is kind of odd. the actual type is:
             * ColumnDefTemplate<HeaderContext<unknown, any>> | undefined
             * this was not very easy to map back existing stuff so it
             * seems like wrapping it in a function fixes the issue.
             */
            header: configuration.header
              ? configuration.header
              : () => configuration?.label,
            accessorKey: fieldName,
            enableSorting: configuration.sortable,
            size: configuration.size,
            className: configuration.dimmable
              ? DIMMABLE_TABLE_CELL_CLASS
              : undefined,
            meta: { align: "left" },

            /**
             * Leverages comparators which can be client defined or built-in to calculate column-based sorting
             * Optional, when the alternative flow criteria is matched for either of the two row values, yield a different client comparator
             * This simplifies the manual code branching if client supplies an alternative `compare` function.
             */
            sortingFn: <T extends RowData>(
              rowA: Row<T>,
              rowB: Row<T>,
              columnId: string
            ) => {
              const rowAValue = rowA.getValue<VT>(columnId);
              const rowBValue = rowB.getValue<VT>(columnId);

              const hasAlternates = Array.isArray(configuration.alternatives);

              if (hasAlternates) {
                const rowAlternativeA =
                  matchPriorityAlternativeConditional(rowAValue);
                const rowAlternativeB =
                  matchPriorityAlternativeConditional(rowBValue);
                if (rowAlternativeA || rowAlternativeB) {
                  const alternator = rowAlternativeA || rowAlternativeB;
                  if (alternator?.compare) {
                    return alternator.compare(rowAValue, rowBValue);
                  }
                }
              }

              return dataType?.compare?.(rowAValue, rowBValue) ?? 0;
            },

            /**
             * The `cell` hook renders a given cell, possibly redirecting to an alternative rendering flow (e.g. null or missing data)
             * Conditionally don't call the renderer when in nullable column mode.
             * Sometimes underlying data is unavailable, and no visualization
             */
            cell: (value: CellContext<RowData, VT>) => {
              const cellValue: VT = value.cell.getValue();
              const alternateConditional =
                matchPriorityAlternativeConditional(cellValue);

              return !alternateConditional
                ? dataType?.render(cellValue) ?? (cellValue as ReactNode)
                : alternateConditional.render(value);
            }
          };
        });
    }, [columns]);

    const initialState = useMemo(() => {
      if (defaultSortColumn) {
        return {
          sorting: [
            {
              id: defaultSortColumn as string,
              desc: false
            }
          ],
          ...rest.initialState
        };
      }

      return rest.initialState;
    }, [defaultSortColumn, rest.initialState]);

    return (
      <ConditionalPagination maxClientPage={maxClientPage?.toString()}>
        <Table
          {...rest} // Magnetic Table properties
          data={data}
          rowSelection={rowSelection}
          columns={columnModel}
          stickyHeader={stickyHeader}
          size="compact"
          className={className}
          isLoading={isLoading}
          initialState={initialState}
        />
      </ConditionalPagination>
    );
  },
  { BulkActionBar: wrappedBulkActionBar }
);
